/**
 * SiflyWebRTCReader - A WebRTC client for receiving WHEP streams
 * Refactored from MediaMTXWebRTCReader with TypeScript support
 */

export interface SiflyWebRTCReaderConfig {
  /** WHEP endpoint URL */
  url: string;
  /** Optional username for basic auth */
  user?: string;
  /** Optional password for basic auth */
  pass?: string;
  /** Optional bearer token for auth */
  token?: string;
  /** Callback when a track is received */
  onTrack?: (event: RTCTrackEvent) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}

type ReaderState = 'getting_codecs' | 'running' | 'restarting' | 'failed' | 'closed';

interface OfferData {
  iceUfrag: string;
  icePwd: string;
  medias: string[];
}

interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
  credentialType?: 'password';
}

export class SiflyWebRTCReader {
  private retryPause = 2000;
  private conf: SiflyWebRTCReaderConfig;
  private state: ReaderState = 'getting_codecs';
  private restartTimeout: number | null = null;
  private pc: RTCPeerConnection | null = null;
  private offerData: OfferData | null = null;
  private sessionUrl: string | null = null;
  private queuedCandidates: RTCIceCandidate[] = [];
  private nonAdvertisedCodecs: string[] = [];

  /**
   * Create a SiflyWebRTCReader.
   * @param conf - Configuration object
   */
  constructor(conf: SiflyWebRTCReaderConfig) {
    this.conf = conf;
    this.#getNonAdvertisedCodecs();
  }

  /**
   * Close the reader and all its resources.
   */
  close(): void {
    this.state = 'closed';

    if (this.pc !== null) {
      this.pc.close();
      this.pc = null;
    }

    if (this.restartTimeout !== null) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }
  }

  static supportsNonAdvertisedCodec(codec: string, fmtp?: string): Promise<boolean> {
    return new Promise((resolve) => {
      const pc = new RTCPeerConnection({ iceServers: [] });
      const mediaType = 'audio';
      let payloadType = '';

      pc.addTransceiver(mediaType, { direction: 'recvonly' });
      pc.createOffer()
        .then((offer) => {
          if (offer.sdp === undefined) {
            throw new Error('SDP not present');
          }
          if (offer.sdp.includes(` ${codec}`)) {
            // codec is advertised, there's no need to add it manually
            throw new Error('already present');
          }

          const sections = offer.sdp.split(`m=${mediaType}`);

          const payloadTypes = sections
            .slice(1)
            .map((s) => s.split('\r\n')[0].split(' ').slice(3))
            .reduce((prev: string[], cur: string[]) => [...prev, ...cur], []);
          payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);

          const lines = sections[1].split('\r\n');
          lines[0] += ` ${payloadType}`;
          lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} ${codec}`);
          if (fmtp !== undefined) {
            lines.splice(lines.length - 1, 0, `a=fmtp:${payloadType} ${fmtp}`);
          }
          sections[1] = lines.join('\r\n');
          const modifiedOffer = { ...offer, sdp: sections.join(`m=${mediaType}`) };
          return pc.setLocalDescription(modifiedOffer);
        })
        .then(() =>
          pc.setRemoteDescription(
            new RTCSessionDescription({
              type: 'answer',
              // eslint-disable-next-line
              sdp:
                `${'v=0\r\n' +
                'o=- 6539324223450680508 0 IN IP4 0.0.0.0\r\n' +
                's=-\r\n' +
                't=0 0\r\n' +
                'a=fingerprint:sha-256 0D:9F:78:15:42:B5:4B:E6:E2:94:3E:5B:37:78:E1:4B:54:59:A3:36:3A:E5:05:EB:27:EE:8F:D2:2D:41:29:25\r\n' +
                `m=${mediaType} 9 UDP/TLS/RTP/SAVPF ${payloadType}\r\n` +
                'c=IN IP4 0.0.0.0\r\n' +
                'a=ice-pwd:7c3bf4770007e7432ee4ea4d697db675\r\n' +
                'a=ice-ufrag:29e036dc\r\n' +
                'a=sendonly\r\n' +
                'a=rtcp-mux\r\n' +
                `a=rtpmap:${payloadType} ${codec}\r\n`}${fmtp !== undefined ? `a=fmtp:${payloadType} ${fmtp}\r\n` : ''}`
            })
          )
        )
        .then(() => {
          resolve(true);
        })
        .catch(() => {
          resolve(false);
        })
        .finally(() => {
          pc.close();
        });
    });
  }

  private static unquoteCredential(v: string): string {
    return JSON.parse(`"${v}"`);
  }

  private static linkToIceServers(links: string | null): IceServer[] {
    return links !== null
      ? links.split(', ').map((link) => {
        const m = link.match(
          /^<(.+?)>; rel="ice-server"(; username="(.*?)"; credential="(.*?)"; credential-type="password")?/i
        );
        if (!m) {
          return { urls: [] };
        }
        const ret: IceServer = {
          urls: [m[1]]
        };

        if (m[3] !== undefined) {
          ret.username = SiflyWebRTCReader.unquoteCredential(m[3]);
          ret.credential = SiflyWebRTCReader.unquoteCredential(m[4]);
          ret.credentialType = 'password';
        }

        return ret;
      })
      : [];
  }

  private static parseOffer(sdp: string): OfferData {
    const ret: OfferData = {
      iceUfrag: '',
      icePwd: '',
      medias: []
    };

    sdp.split('\r\n').forEach((line) => {
      if (line.startsWith('m=')) {
        ret.medias.push(line.slice('m='.length));
      } else if (ret.iceUfrag === '' && line.startsWith('a=ice-ufrag:')) {
        ret.iceUfrag = line.slice('a=ice-ufrag:'.length);
      } else if (ret.icePwd === '' && line.startsWith('a=ice-pwd:')) {
        ret.icePwd = line.slice('a=ice-pwd:'.length);
      }
    });

    return ret;
  }

  private static reservePayloadType(payloadTypes: string[]): string {
    // everything is valid between 30 and 127, except for interval between 64 and 95
    // https://chromium.googlesource.com/external/webrtc/+/refs/heads/master/call/payload_type.h#29
    for (let i = 30; i <= 127; i++) {
      if ((i <= 63 || i >= 96) && !payloadTypes.includes(i.toString())) {
        const pl = i.toString();
        payloadTypes.push(pl);
        return pl;
      }
    }
    throw Error('unable to find a free payload type');
  }

  private static enableStereoPcmau(payloadTypes: string[], section: string): string {
    const lines = section.split('\r\n');

    let payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} PCMU/8000/2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} PCMA/8000/2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    return lines.join('\r\n');
  }

  private static enableMultichannelOpus(payloadTypes: string[], section: string): string {
    const lines = section.split('\r\n');

    let payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/3`);
    lines.splice(lines.length - 1, 0, `a=fmtp:${payloadType} channel_mapping=0,2,1;num_streams=2;coupled_streams=1`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/4`);
    lines.splice(lines.length - 1, 0, `a=fmtp:${payloadType} channel_mapping=0,1,2,3;num_streams=2;coupled_streams=2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/5`);
    lines.splice(
      lines.length - 1,
      0,
      `a=fmtp:${payloadType} channel_mapping=0,4,1,2,3;num_streams=3;coupled_streams=2`
    );
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/6`);
    lines.splice(
      lines.length - 1,
      0,
      `a=fmtp:${payloadType} channel_mapping=0,4,1,2,3,5;num_streams=4;coupled_streams=2`
    );
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/7`);
    lines.splice(
      lines.length - 1,
      0,
      `a=fmtp:${payloadType} channel_mapping=0,4,1,2,3,5,6;num_streams=4;coupled_streams=4`
    );
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} multiopus/48000/8`);
    lines.splice(
      lines.length - 1,
      0,
      `a=fmtp:${payloadType} channel_mapping=0,6,1,4,5,2,3,7;num_streams=5;coupled_streams=4`
    );
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    return lines.join('\r\n');
  }

  private static enableL16(payloadTypes: string[], section: string): string {
    const lines = section.split('\r\n');

    let payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} L16/8000/2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} L16/16000/2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    payloadType = SiflyWebRTCReader.reservePayloadType(payloadTypes);
    lines[0] += ` ${payloadType}`;
    lines.splice(lines.length - 1, 0, `a=rtpmap:${payloadType} L16/48000/2`);
    lines.splice(lines.length - 1, 0, `a=rtcp-fb:${payloadType} transport-cc`);

    return lines.join('\r\n');
  }

  private static enableStereoOpus(section: string): string {
    let opusPayloadFormat = '';
    const lines = section.split('\r\n');

    const opusLine = lines.find((line) => line.startsWith('a=rtpmap:') && line.toLowerCase().includes('opus/'));
    if (opusLine) {
      const parts = opusLine.slice('a=rtpmap:'.length).split(' ');
      [opusPayloadFormat] = parts;
    }

    if (opusPayloadFormat === '') {
      return section;
    }

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith(`a=fmtp:${opusPayloadFormat} `)) {
        if (!lines[i].includes('stereo')) {
          lines[i] += ';stereo=1';
        }
        if (!lines[i].includes('sprop-stereo')) {
          lines[i] += ';sprop-stereo=1';
        }
      }
    }

    return lines.join('\r\n');
  }

  private static editOffer(sdp: string, nonAdvertisedCodecs: string[]): string {
    const sections = sdp.split('m=');

    const payloadTypes = sections
      .slice(1)
      .map((s) => s.split('\r\n')[0].split(' ').slice(3))
      .reduce((prev: string[], cur: string[]) => [...prev, ...cur], []);

    for (let i = 1; i < sections.length; i++) {
      if (sections[i].startsWith('audio')) {
        sections[i] = SiflyWebRTCReader.enableStereoOpus(sections[i]);

        if (nonAdvertisedCodecs.includes('pcma/8000/2')) {
          sections[i] = SiflyWebRTCReader.enableStereoPcmau(payloadTypes, sections[i]);
        }
        if (nonAdvertisedCodecs.includes('multiopus/48000/6')) {
          sections[i] = SiflyWebRTCReader.enableMultichannelOpus(payloadTypes, sections[i]);
        }
        if (nonAdvertisedCodecs.includes('L16/48000/2')) {
          sections[i] = SiflyWebRTCReader.enableL16(payloadTypes, sections[i]);
        }

        break;
      }
    }

    return sections.join('m=');
  }

  private static generateSdpFragment(od: OfferData, candidates: RTCIceCandidate[]): string {
    const candidatesByMedia: Record<number, RTCIceCandidate[]> = {};
    candidates.forEach((candidate) => {
      const mid = candidate.sdpMLineIndex ?? 0;
      if (candidatesByMedia[mid] === undefined) {
        candidatesByMedia[mid] = [];
      }
      candidatesByMedia[mid].push(candidate);
    });

    let frag = `a=ice-ufrag:${od.iceUfrag}\r\na=ice-pwd:${od.icePwd}\r\n`;

    od.medias.forEach((media, mid) => {
      if (candidatesByMedia[mid] !== undefined) {
        frag += `m=${media}\r\na=mid:${mid}\r\n`;

        candidatesByMedia[mid].forEach((candidate) => {
          frag += `a=${candidate.candidate}\r\n`;
        });
      }
    });

    return frag;
  }

  #handleError(err: unknown): void {
    if (this.state === 'running') {
      if (this.pc !== null) {
        this.pc.close();
        this.pc = null;
      }

      this.offerData = null;

      if (this.sessionUrl !== null) {
        fetch(this.sessionUrl, {
          method: 'DELETE'
        });
        this.sessionUrl = null;
      }

      this.queuedCandidates = [];
      this.state = 'restarting';

      this.restartTimeout = window.setTimeout(() => {
        this.restartTimeout = null;
        this.state = 'running';
        this.#start();
      }, this.retryPause) as unknown as number;

      if (this.conf.onError !== undefined) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        this.conf.onError(`${errorMessage}, retrying in some seconds`);
      }
    } else if (this.state === 'getting_codecs') {
      this.state = 'failed';

      if (this.conf.onError !== undefined) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        this.conf.onError(errorMessage);
      }
    }
  }

  #getNonAdvertisedCodecs(): void {
    Promise.all(
      [
        ['pcma/8000/2'],
        ['multiopus/48000/6', 'channel_mapping=0,4,1,2,3,5;num_streams=4;coupled_streams=2'],
        ['L16/48000/2']
      ].map((c) => SiflyWebRTCReader.supportsNonAdvertisedCodec(c[0], c[1]).then((r) => (r ? c[0] : false)))
    )
      .then((c) => c.filter((e): e is string => e !== false))
      .then((codecs) => {
        if (this.state !== 'getting_codecs') {
          throw new Error('closed');
        }

        this.nonAdvertisedCodecs = codecs;
        this.state = 'running';
        this.#start();
      })
      .catch((err) => {
        this.#handleError(err);
      });
  }

  #start(): void {
    this.#requestICEServers()
      .then((iceServers) => this.#setupPeerConnection(iceServers))
      .then((offer) => this.#sendOffer(offer))
      .then((answer) => this.#setAnswer(answer))
      .catch((err) => {
        this.#handleError(err.toString());
      });
  }

  #authHeader(): Record<string, string> {
    if (this.conf.user !== undefined && this.conf.user !== '') {
      const credentials = btoa(`${this.conf.user}:${this.conf.pass ?? ''}`);
      return { Authorization: `Basic ${credentials}` };
    }
    if (this.conf.token !== undefined && this.conf.token !== '') {
      return { Authorization: `Bearer ${this.conf.token}` };
    }
    return {};
  }

  #requestICEServers(): Promise<IceServer[]> {
    return fetch(this.conf.url, {
      method: 'OPTIONS',
      headers: {
        ...this.#authHeader()
      }
    }).then((res) => SiflyWebRTCReader.linkToIceServers(res.headers.get('Link')));
  }

  #setupPeerConnection(iceServers: IceServer[]): Promise<string> {
    if (this.state !== 'running') {
      throw new Error('closed');
    }

    this.pc = new RTCPeerConnection({
      iceServers: iceServers as RTCConfiguration['iceServers'],
      // https://webrtc.org/getting-started/unified-plan-transition-guide
      // @ts-expect-error - sdpSemantics is deprecated but still needed for some browsers
      sdpSemantics: 'unified-plan'
    });

    const direction: RTCRtpTransceiverDirection = 'recvonly';
    this.pc.addTransceiver('video', { direction });
    this.pc.addTransceiver('audio', { direction });

    this.pc.onicecandidate = (evt) => this.#onLocalCandidate(evt);
    this.pc.onconnectionstatechange = () => this.#onConnectionState();
    this.pc.ontrack = (evt) => this.#onTrack(evt);

    return this.pc.createOffer().then((offer) => {
      if (!offer.sdp) {
        throw new Error('SDP not present in offer');
      }
      const modifiedSdp = SiflyWebRTCReader.editOffer(offer.sdp, this.nonAdvertisedCodecs);
      const modifiedOffer = { ...offer, sdp: modifiedSdp };
      this.offerData = SiflyWebRTCReader.parseOffer(modifiedSdp);

      return this.pc!.setLocalDescription(modifiedOffer).then(() => modifiedSdp);
    });
  }

  #sendOffer(offer: string): Promise<string> {
    if (this.state !== 'running') {
      throw new Error('closed');
    }

    return fetch(this.conf.url, {
      method: 'POST',
      headers: {
        ...this.#authHeader(),
        'Content-Type': 'application/sdp'
      },
      body: offer
    }).then((res) => {
      switch (res.status) {
        case 201:
          break;
        case 404:
          throw new Error('stream not found');
        case 400:
          return res.json().then((e: { error?: string }) => {
            throw new Error(e.error ?? 'Bad request');
          });
        default:
          throw new Error(`bad status code ${res.status}`);
      }

      const locationHeader = res.headers.get('location');
      if (!locationHeader) {
        throw new Error('Location header not present in response');
      }
      this.sessionUrl = new URL(locationHeader, this.conf.url).toString();

      return res.text();
    });
  }

  #setAnswer(answer: string): Promise<void> {
    if (this.state !== 'running') {
      throw new Error('closed');
    }

    return this.pc!.setRemoteDescription(
      new RTCSessionDescription({
        type: 'answer',
        sdp: answer
      })
    ).then(() => {
      if (this.state !== 'running') {
        return;
      }

      if (this.queuedCandidates.length !== 0) {
        this.#sendLocalCandidates(this.queuedCandidates);
        this.queuedCandidates = [];
      }
    });
  }

  #onLocalCandidate(evt: RTCPeerConnectionIceEvent): void {
    if (this.state !== 'running') {
      return;
    }

    if (evt.candidate !== null) {
      if (this.sessionUrl === null) {
        this.queuedCandidates.push(evt.candidate);
      } else {
        this.#sendLocalCandidates([evt.candidate]);
      }
    }
  }

  #sendLocalCandidates(candidates: RTCIceCandidate[]): void {
    if (!this.sessionUrl || !this.offerData) {
      return;
    }

    fetch(this.sessionUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/trickle-ice-sdpfrag',
        'If-Match': '*'
      },
      body: SiflyWebRTCReader.generateSdpFragment(this.offerData, candidates)
    })
      .then((res) => {
        switch (res.status) {
          case 204:
            break;
          case 404:
            throw new Error('stream not found');
          default:
            throw new Error(`bad status code ${res.status}`);
        }
      })
      .catch((err) => {
        this.#handleError(err.toString());
      });
  }

  #onConnectionState(): void {
    if (this.state !== 'running') {
      return;
    }

    // "closed" can arrive before "failed" and without
    // the close() method being called at all.
    // It happens when the other peer sends a termination
    // message like a DTLS CloseNotify.
    if (this.pc && (this.pc.connectionState === 'failed' || this.pc.connectionState === 'closed')) {
      this.#handleError('peer connection closed');
    }
  }

  #onTrack(evt: RTCTrackEvent): void {
    if (this.conf.onTrack !== undefined) {
      this.conf.onTrack(evt);
    }
  }
}
